/**
 * Low-level HTTP client for the cloud projects/assets API, plus the two
 * "steady state" sync operations (pull a cloud project down, push local
 * edits up). The one-time local→cloud upload flow lives in
 * `migrateToCloud.ts`, which is built on top of the primitives here.
 *
 * The app must keep working fully offline as a guest — nothing in this
 * module may be imported/executed unless the caller already knows the
 * project is cloud-linked and the user is authenticated.
 */
import { safeParseProject, type PhotobookProject } from '@fotoboek/domain';
import { assetBlobStore } from '../storage/blobStore';
import {
  createProjectRecord,
  findLocalProjectIdByCloudProjectId,
  getProjectMeta,
  saveProject,
  updateProjectMetaFields,
} from '../storage/projectRepository';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

export class CloudApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'CloudApiError';
    this.status = status;
    this.code = code;
  }
}

async function cloudRequest<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch {
    throw new CloudApiError('Geen verbinding met de server.', 0, 'OFFLINE');
  }
  if (!res.ok) {
    let payload: { error?: { message?: string; code?: string } } | null = null;
    try {
      payload = await res.json();
    } catch {
      // Non-JSON error body — keep default message below.
    }
    throw new CloudApiError(
      payload?.error?.message ?? 'Er ging iets mis bij de synchronisatie met de cloud.',
      res.status,
      payload?.error?.code,
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export type CloudProjectDetail = {
  id: string;
  title: string;
  description: string | null;
  productProfileId: string;
  revision: number;
  status: 'draft' | 'ready' | 'exported' | 'archived';
  createdAt: string;
  updatedAt: string;
  document: unknown;
  ownerId: string;
};

export type CloudProjectSummary = {
  id: string;
  title: string;
  description: string | null;
  productProfileId: string;
  revision: number;
  status: string;
  createdAt: string;
  updatedAt: string;
};

export async function createCloudProject(input: {
  title: string;
  description?: string;
  productProfileId?: string;
  pageCount?: number;
}): Promise<CloudProjectDetail> {
  return cloudRequest<CloudProjectDetail>('/projects', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function getCloudProject(cloudProjectId: string): Promise<CloudProjectDetail> {
  return cloudRequest<CloudProjectDetail>(`/projects/${cloudProjectId}`, { method: 'GET' });
}

export async function listCloudProjects(
  pagination: { page?: number; pageSize?: number } = {},
): Promise<{ items: CloudProjectSummary[]; total: number; page: number; pageSize: number }> {
  const page = pagination.page ?? 1;
  const pageSize = pagination.pageSize ?? 50;
  return cloudRequest(`/projects?page=${page}&pageSize=${pageSize}`, { method: 'GET' });
}

export async function updateCloudProjectMeta(
  cloudProjectId: string,
  patch: {
    title?: string;
    description?: string | null;
    status?: 'draft' | 'ready' | 'exported' | 'archived';
  },
): Promise<CloudProjectDetail> {
  return cloudRequest<CloudProjectDetail>(`/projects/${cloudProjectId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function deleteCloudProject(cloudProjectId: string): Promise<void> {
  await cloudRequest<void>(`/projects/${cloudProjectId}`, { method: 'DELETE' });
}

export type SyncPushResponse = { revision: number; updatedAt: string; status: string };

/** Raw sync call — see apps/api/src/routes/projects.ts `POST /:id/sync`. */
export async function syncCloudProject(
  cloudProjectId: string,
  input: { baseRevision: number; document: unknown; deviceLabel?: string },
): Promise<SyncPushResponse> {
  return cloudRequest<SyncPushResponse>(`/projects/${cloudProjectId}/sync`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export type InitAssetUploadResult = {
  assetId: string;
  objectKey: string;
  uploadUrl: string | null;
  alreadyUploaded: boolean;
};

export async function initCloudAssetUpload(input: {
  mimeType: string;
  byteSize: number;
  checksumSha256: string;
  widthPx?: number;
  heightPx?: number;
}): Promise<InitAssetUploadResult> {
  return cloudRequest<InitAssetUploadResult>('/assets/init', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** PUT the raw bytes to the presigned S3 URL returned by `initCloudAssetUpload`. */
export async function uploadAssetBinary(
  uploadUrl: string,
  blob: Blob,
  mimeType: string,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(uploadUrl, {
      method: 'PUT',
      body: blob,
      headers: { 'Content-Type': mimeType },
    });
  } catch {
    throw new CloudApiError(
      'Uploaden van foto naar cloudopslag is mislukt (geen verbinding).',
      0,
      'UPLOAD_FAILED',
    );
  }
  if (!res.ok) {
    throw new CloudApiError(
      `Uploaden van foto naar cloudopslag is mislukt (${res.status}).`,
      res.status,
      'UPLOAD_FAILED',
    );
  }
}

export type CloudAssetRecord = {
  id: string;
  objectKey: string;
  thumbnailKey: string | null;
  mimeType: string;
  widthPx: number | null;
  heightPx: number | null;
  uploadStatus: 'pending' | 'uploaded';
};

export async function completeCloudAssetUpload(assetId: string): Promise<CloudAssetRecord> {
  return cloudRequest<CloudAssetRecord>(`/assets/${assetId}/complete`, { method: 'POST' });
}

export type CloudAssetWithUrls = CloudAssetRecord & {
  originalUrl: string;
  thumbnailUrl: string | null;
};

export async function getCloudAssetWithUrls(assetId: string): Promise<CloudAssetWithUrls> {
  return cloudRequest<CloudAssetWithUrls>(`/assets/${assetId}`, { method: 'GET' });
}

export async function deleteCloudAsset(assetId: string): Promise<void> {
  await cloudRequest<void>(`/assets/${assetId}`, { method: 'DELETE' });
}

async function downloadAsBlob(url: string, mimeType: string): Promise<Blob> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Download van foto is mislukt (status ${res.status}).`);
  const buffer = await res.arrayBuffer();
  return new Blob([buffer], { type: mimeType });
}

export type PullResult =
  | { status: 'ok'; project: PhotobookProject; isNewLocalProject: boolean }
  | { status: 'error'; message: string };

/**
 * Downloads a cloud project and materializes it as a local, cloud-linked
 * project (Dexie + OPFS/IndexedDB blobs), so it can be opened in the same
 * editor as any local project. Re-pulling an already-linked project reuses
 * its existing local id and skips assets whose blobs are already present.
 */
export async function pullCloudProject(cloudProjectId: string): Promise<PullResult> {
  let detail: CloudProjectDetail;
  try {
    detail = await getCloudProject(cloudProjectId);
  } catch (err) {
    return {
      status: 'error',
      message:
        err instanceof CloudApiError ? err.message : 'Kan dit project niet uit de cloud ophalen.',
    };
  }

  const parsed = safeParseProject(detail.document);
  if (!parsed.success) {
    return { status: 'error', message: parsed.error };
  }

  const existingLocalId = await findLocalProjectIdByCloudProjectId(cloudProjectId);
  const localProjectId = existingLocalId ?? crypto.randomUUID();
  const isNewLocalProject = !existingLocalId;

  const project: PhotobookProject = {
    ...parsed.data,
    id: localProjectId,
    ownerMode: 'cloud',
    cloudProjectId,
  };

  for (const asset of project.assets) {
    if (!asset.cloudAssetId) continue;
    const existingBlob = await assetBlobStore.get(localProjectId, asset.id, 'original');
    if (existingBlob) continue;
    try {
      const withUrls = await getCloudAssetWithUrls(asset.cloudAssetId);
      const originalBlob = await downloadAsBlob(withUrls.originalUrl, asset.mimeType);
      await assetBlobStore.put(localProjectId, asset.id, 'original', originalBlob);
      if (withUrls.thumbnailUrl) {
        const thumbBlob = await downloadAsBlob(withUrls.thumbnailUrl, 'image/jpeg');
        await assetBlobStore.put(localProjectId, asset.id, 'thumbnail', thumbBlob);
      }
    } catch (err) {
      // A single failed photo download shouldn't block opening the whole
      // project — its frame simply renders empty until the user retries.
      console.error(`Kan foto ${asset.id} niet downloaden uit de cloud`, err);
    }
  }

  await saveProject(project);
  await updateProjectMetaFields(localProjectId, {
    cloudProjectId,
    cloudRevision: detail.revision,
    syncState: 'synced',
    syncError: undefined,
  });

  return { status: 'ok', project, isNewLocalProject };
}

/**
 * Pure decision helper: given a local project that just hit a 409 on push,
 * and the server's current document, build (a) a local recovery copy that
 * preserves the user's unsynced local edits, and (b) the project state to
 * adopt locally (server wins on the linked id, but keeps the local storage
 * key stable so open editors/routes don't need to change).
 */
export function buildConflictRecoveryProject(localProject: PhotobookProject): PhotobookProject {
  return {
    ...localProject,
    id: crypto.randomUUID(),
    ownerMode: 'local',
    cloudProjectId: undefined,
    title: `${localProject.title} (herstelkopie)`,
    updatedAt: new Date().toISOString(),
  };
}

export function adoptServerProjectLocally(
  localProject: PhotobookProject,
  serverProject: PhotobookProject,
): PhotobookProject {
  return {
    ...serverProject,
    id: localProject.id,
    ownerMode: 'cloud',
    cloudProjectId: localProject.cloudProjectId,
  };
}

export type PushResult =
  | { status: 'synced'; project: PhotobookProject }
  | { status: 'conflict'; recoveryProject: PhotobookProject; project: PhotobookProject }
  | { status: 'offline' }
  | { status: 'not-linked' }
  | { status: 'error'; message: string };

async function resolveSyncConflict(localProject: PhotobookProject): Promise<PushResult> {
  const cloudProjectId = localProject.cloudProjectId;
  if (!cloudProjectId) return { status: 'not-linked' };

  let detail: CloudProjectDetail;
  try {
    detail = await getCloudProject(cloudProjectId);
  } catch {
    await updateProjectMetaFields(localProject.id, {
      syncState: 'error',
      syncError:
        'Synchronisatieconflict — kon de serverversie niet ophalen. Probeer het later opnieuw.',
    });
    return {
      status: 'error',
      message:
        'Er is een synchronisatieconflict, maar de serverversie kan nu niet worden opgehaald.',
    };
  }

  const parsed = safeParseProject(detail.document);
  if (!parsed.success) {
    return { status: 'error', message: parsed.error };
  }

  const recoveryProject = buildConflictRecoveryProject(localProject);
  const adopted = adoptServerProjectLocally(localProject, parsed.data);

  await createProjectRecord(recoveryProject);
  await saveProject(adopted);
  await updateProjectMetaFields(localProject.id, {
    cloudRevision: detail.revision,
    syncState: 'error',
    syncError:
      'De serverversie is overgenomen; jouw wijzigingen zijn bewaard als herstelkopie in “Op dit apparaat”.',
  });

  return { status: 'conflict', recoveryProject, project: adopted };
}

/**
 * Pushes local edits to a cloud-linked project using optimistic concurrency
 * control. Never throws — callers (autosync, manual "sync now") should
 * inspect the returned status instead of using try/catch.
 */
export async function pushProjectSync(
  project: PhotobookProject,
  opts?: { deviceLabel?: string },
): Promise<PushResult> {
  if (project.ownerMode !== 'cloud' || !project.cloudProjectId) {
    return { status: 'not-linked' };
  }

  const meta = await getProjectMeta(project.id);
  const baseRevision = meta?.cloudRevision ?? 0;

  try {
    const result = await syncCloudProject(project.cloudProjectId, {
      baseRevision,
      document: project,
      deviceLabel: opts?.deviceLabel,
    });
    const updated: PhotobookProject = {
      ...project,
      revision: result.revision,
      updatedAt: result.updatedAt,
    };
    await saveProject(updated);
    await updateProjectMetaFields(project.id, {
      cloudRevision: result.revision,
      syncState: 'synced',
      syncError: undefined,
    });
    return { status: 'synced', project: updated };
  } catch (err) {
    if (err instanceof CloudApiError && err.status === 0) {
      await updateProjectMetaFields(project.id, { syncState: 'pending' });
      return { status: 'offline' };
    }
    if (err instanceof CloudApiError && err.status === 409) {
      return resolveSyncConflict(project);
    }
    const message =
      err instanceof CloudApiError ? err.message : 'Synchroniseren met de cloud is mislukt.';
    await updateProjectMetaFields(project.id, { syncState: 'error', syncError: message });
    return { status: 'error', message };
  }
}
